import { createContext } from "react";
import { PortalScope, UNSCOPED_PORTAL } from "@/services/portalScope.service";

/**
 * The one brand this affiliate session is confined to, when it is.
 * `HOST`: the page is a brand's portal. `SCOPE`: the affiliate was recruited
 * through a portal and is on the marketplace apex. Null: open marketplace.
 */
export interface PortalBrandRef {
  id: string;
  username: string | null;
  name: string | null;
  source: "HOST" | "SCOPE";
}

export interface PortalScopeContextType {
  scope: PortalScope;
  /** True until the first fetch settles — callers should not act on `scope` yet. */
  isLoading: boolean;
  /**
   * The brand every list, page and request is confined to, or null. Route
   * guards use it to bounce a brand page that is not this brand.
   */
  portalBrand: PortalBrandRef | null;
  /**
   * Hide every marketplace entry point: brand search, the dashboard brand
   * carousel, discovery CTAs. On a portal host this is true immediately; for
   * the scope source it is false while loading, so unscoped affiliates never
   * see marketplace UI flicker in and out.
   */
  isMarketplaceHidden: boolean;
  /**
   * Hide surfaces that name *other brands* — the dashboard brand marquee and
   * anything else that renders the wider platform's brands.
   *
   * Unlike `isMarketplaceHidden` this is **true while loading**: showing a
   * link a scoped affiliate cannot follow is a cosmetic bug, but painting a
   * competitor's logo into a white-label portal for the length of a fetch is
   * the leak this whole ticket exists to close. The cost is that unscoped
   * affiliates see the marquee a beat later, which the dashboard already
   * covers with a skeleton.
   */
  isCrossBrandHidden: boolean;
  /** True when `portalBrand` is set — the session is a one-brand sandbox. */
  isConfined: boolean;
  /**
   * True inside PortalScopeProvider. The default context — brand/admin
   * shells, auth pages — reports false, so consumers (usePortalBranding) can
   * tell "definitely unscoped" from "scope is unknowable here".
   */
  hasProvider: boolean;
}

export const PortalScopeContext = createContext<PortalScopeContextType>({
  scope: UNSCOPED_PORTAL,
  isLoading: true,
  portalBrand: null,
  isMarketplaceHidden: false,
  isCrossBrandHidden: false,
  isConfined: false,
  hasProvider: false,
});
